import React, { useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

export const EMPTY_PART = { name: "", part_number: "", quantity: 1, cost: "" };

// Rows without a name are dropped (the backend does the same).
export function partsForPayload(parts) {
  return (parts || [])
    .filter((p) => (p.name || "").trim())
    .map((p) => ({
      name: p.name.trim(),
      part_number: (p.part_number || "").trim(),
      quantity: p.quantity === "" || p.quantity == null ? 1 : p.quantity,
      cost: p.cost === "" || p.cost == null ? null : p.cost,
    }));
}

// Optional "what did the fix cost" block on a maintenance ticket. Starts
// collapsed unless the ticket already has values.
function CostAndPartsSection({
  repairCost,
  onRepairCostChange,
  parts,
  onPartsChange,
  textFieldSx,
  onSave,
  isSaving,
}) {
  const hasValues = Boolean(repairCost) || (parts || []).length > 0;
  const [isOpen, setIsOpen] = useState(hasValues);
  // Open once a ticket's saved values arrive (they load after mount), unless
  // the user has already opened or closed the section themselves.
  const userToggled = useRef(false);
  useEffect(() => {
    if (hasValues && !userToggled.current) setIsOpen(true);
  }, [hasValues]);

  const updatePart = (index, key, value) =>
    onPartsChange(parts.map((p, i) => (i === index ? { ...p, [key]: value } : p)));

  return (
    <div className="mt-6 pt-5 border-t border-slate-200">
      <button
        type="button"
        className="w-full flex items-center justify-between text-left"
        onClick={() => {
          userToggled.current = true;
          setIsOpen(!isOpen);
        }}
        aria-expanded={isOpen}
      >
        <span>
          <span className="block text-sm font-semibold text-slate-700">Cost and parts</span>
          <span className="block text-xs text-slate-400 mt-0.5">
            Optional. Adds up each asset's repair spend and the parts it keeps needing.
          </span>
        </span>
        {isOpen ? (
          <ExpandLessIcon sx={{ color: "#64748b" }} />
        ) : (
          <ExpandMoreIcon sx={{ color: "#64748b" }} />
        )}
      </button>

      {isOpen && (
        <>
          <div className="mt-4">
            <p className="text-sm text-slate-500">Total repair cost</p>
            <div className="mt-1">
              <TextField
                fullWidth
                size="small"
                value={repairCost}
                onChange={(e) => onRepairCostChange(e.target.value)}
                placeholder="0.00"
                inputProps={{ inputMode: "decimal", "aria-label": "Total repair cost" }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={textFieldSx}
              />
            </div>
          </div>

          <div className="mt-5">
            <p className="text-sm text-slate-500">Parts used</p>
            {(parts || []).map((part, i) => (
              <div key={i} className="mt-2 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-start gap-2">
                  <TextField
                    fullWidth
                    size="small"
                    value={part.name}
                    onChange={(e) => updatePart(i, "name", e.target.value)}
                    placeholder="Part name"
                    inputProps={{ maxLength: 255, "aria-label": `Part ${i + 1} name` }}
                    sx={textFieldSx}
                  />
                  <IconButton
                    size="small"
                    aria-label={`Remove part ${i + 1}`}
                    onClick={() => onPartsChange(parts.filter((_p, j) => j !== i))}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
                <div className="mt-2 flex gap-2">
                  <TextField
                    size="small"
                    value={part.part_number || ""}
                    onChange={(e) => updatePart(i, "part_number", e.target.value)}
                    placeholder="Part #"
                    inputProps={{ maxLength: 255, "aria-label": `Part ${i + 1} number` }}
                    sx={{ ...textFieldSx, flex: 2 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={part.quantity}
                    onChange={(e) => updatePart(i, "quantity", e.target.value)}
                    inputProps={{ min: 1, "aria-label": `Part ${i + 1} quantity` }}
                    sx={{ ...textFieldSx, flex: 1 }}
                  />
                  <TextField
                    size="small"
                    value={part.cost ?? ""}
                    onChange={(e) => updatePart(i, "cost", e.target.value)}
                    placeholder="Cost"
                    inputProps={{ inputMode: "decimal", "aria-label": `Part ${i + 1} cost` }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={{ ...textFieldSx, flex: 1.5 }}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              className="mt-2 flex items-center gap-1 text-sm font-medium text-violet-700 hover:text-violet-800"
              onClick={() => onPartsChange([...(parts || []), { ...EMPTY_PART }])}
            >
              <AddIcon fontSize="small" />
              Add part
            </button>
          </div>

          {onSave && (
            <button
              type="button"
              disabled={isSaving}
              onClick={onSave}
              className="mt-4 w-full py-2.5 rounded-lg border border-violet-700 text-violet-700 hover:bg-violet-50 text-sm font-medium disabled:opacity-60"
            >
              {isSaving ? "Saving..." : "Save cost and parts"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default CostAndPartsSection;
